// Portal-aware nav config builder. Returns a memoized list of nav items
// the Layout renders into the Sidebar + Spotlight.
import {
  ArrowUUpLeftIcon,
  ChartPieIcon,
  FileTextIcon,
  GearSixIcon,
  ListBulletsIcon,
  PackageIcon,
  ShoppingCartSimpleIcon,
  TrayIcon,
  TruckIcon,
  UserIcon,
} from "@phosphor-icons/react"
import { useMemo } from "react"
import useSWR from "swr"
import { GovernanceConfigApi } from "../../../apis"
import { LayoutPermissions } from "./use-layout-permissions"

// Phosphor icon component shape — Sidebar accepts the bare component
// reference and renders it itself.
export type NavIcon = typeof ChartPieIcon

export interface NavItem {
  titleKey: string
  icon: NavIcon
  link: string
  view: boolean
}

export type Portal = "BUYER" | "SUPPLIER"

export function useNavigation(perms: LayoutPermissions, portal: Portal): NavItem[] {
  // §27.15 — resolved governance config gates the Орлого (inbound) entry.
  // Fetched for SUPPLIER only (shared ["governance-config"] SWR cache). Missing
  // config (loading, or no BIZ_GOV_VIEW) defaults to showing inbound, so the
  // gate is a safe progressive enhancement, never a lock-out.
  const { data: govConfig } = useSWR(
    portal === "SUPPLIER" ? ["governance-config"] : null,
    () => GovernanceConfigApi.get(),
    { revalidateOnFocus: false },
  )
  const inboundDisabledByConfig =
    !!govConfig &&
    !govConfig.inboundFlow?.crossWarehouseSourcing?.enabled &&
    !govConfig.inboundFlow?.externalPickupEnabled

  return useMemo(() => {
    if (portal === "BUYER") {
      return [
        // "/" redirects a buyer to the catalogue (App.tsx HomeRedirect), so the
        // buyer's dashboard link has to be the explicit route.
        { titleKey: "nav.dashboard", icon: ChartPieIcon, link: "/dashboard", view: perms.dashboard },
        { titleKey: "nav.order", icon: ShoppingCartSimpleIcon, link: "/buyer-sales/new", view: perms.orderEdit || perms.orderSave || perms.orderSend },
        { titleKey: "nav.purchase", icon: ListBulletsIcon, link: "/buyer-sales", view: perms.netList },
        { titleKey: "nav.quotation", icon: FileTextIcon, link: "/received-quotation", view: true },
        { titleKey: "nav.contractSuppliers", icon: UserIcon, link: "/buyer-customer", view: perms.netList },
        // mvp-returns Phase 2 — buyer returns hub (gated RETURN_VIEW).
        { titleKey: "nav.returns", icon: ArrowUUpLeftIcon, link: "/buyer-app/my-returns", view: perms.returnView },
      ]
    }
    return [
      { titleKey: "nav.dashboard", icon: ChartPieIcon, link: "/", view: perms.dashboard },
      { titleKey: "nav.salesOrder", icon: ListBulletsIcon, link: "/supplier-sales", view: perms.split || perms.list },
      { titleKey: "nav.customerOrder", icon: PackageIcon, link: "/supplier-purchase", view: perms.backOrder || perms.list },
      { titleKey: "nav.dropshipping", icon: PackageIcon, link: "/supplier-drop-shipping", view: perms.dropshipping },
      { titleKey: "nav.deliveryManagement", icon: TruckIcon, link: "/supplier-delivery", view: perms.dnDraftList || perms.dnAssignedList || perms.dnDeliveredList || perms.dnMng || perms.routePlan },
      { titleKey: "nav.shippingManagement", icon: PackageIcon, link: "/supplier-shipping", view: perms.psList },
      { titleKey: "nav.inbound", icon: TrayIcon, link: "/inbound", view: perms.inboundView && !inboundDisabledByConfig },
      { titleKey: "nav.contractCustomer", icon: UserIcon, link: "/supplier-customer", view: perms.netList },
      { titleKey: "nav.returns", icon: ArrowUUpLeftIcon, link: "/returns", view: perms.list },
      { titleKey: "nav.vehicle", icon: TruckIcon, link: "/vehicle", view: perms.vehicleView },
      { titleKey: "nav.settings", icon: GearSixIcon, link: "/settings", view: perms.settingsView },
    ]
  }, [portal, perms, inboundDisabledByConfig])
}
